import DOMPurify from "dompurify";

/**
 * Nettoyage HTML pour les contenus rédigés par les administrateurs
 * (description de campagne, message email custom, etc.).
 *
 * Pourquoi : un compte admin compromis ou un texte généré par IA pourrait
 * injecter <script>, <iframe>, ou un onerror=… dans la description d'une
 * campagne. Cette description est rendue via dangerouslySetInnerHTML sur
 * la page publique → le code s'exécuterait chez chaque visiteur, capable
 * de voler les sessions stockées en sessionStorage.
 *
 * On autorise un sous-ensemble sain de mise en forme (paragraphes,
 * gras, italique, listes, liens) et on supprime tout le reste.
 *
 * Les liens passés en attribut href sont validés par DOMPurify (refuse
 * automatiquement javascript:, data:, vbscript: …). On force aussi
 * target="_blank" + rel="noopener noreferrer" pour la sécurité.
 */
const ALLOWED_TAGS = [
  "p", "br", "strong", "b", "em", "i", "u",
  "ul", "ol", "li", "a", "span",
  "h2", "h3", "h4",
];

const ALLOWED_ATTR = ["href", "target", "rel", "class"];

// Hook qui ajoute target=_blank + rel=noopener sur les <a> — empêche
// qu'un lien malveillant ouvre la page sur le même tab (window.opener).
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

export function sanitizeRichText(html) {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
  });
}

/**
 * La description contient-elle du HTML (balise ou entité comme « &amp; ») ?
 *
 * L'assistant de création enregistre la description en TEXTE BRUT (zone de
 * texte avec retours à la ligne) ; les anciennes descriptions générées par
 * IA sont en HTML. Texte brut → on l'affiche tel quel (React échappe tout,
 * retours à la ligne gardés) ; HTML → sanitizeRichText, comme avant.
 * « 5 < 10 » n'est pas une balise (il faut une lettre juste après « < »).
 */
const RX_HTML = /<\/?[a-z][^>]*>|&(?:#\d+|#x[0-9a-f]+|[a-z]+);/i;

export function contientDuHtml(texte) {
  return RX_HTML.test(String(texte || ""));
}

export default sanitizeRichText;
